import { Platform } from 'react-native';
import storage from '@lib/utils/storage';
import Sentry from '@lib/analytics/sentry';

export const isWeb = typeof window !== 'undefined' && Platform.OS === 'web';

const DEVICE_FINGERPRINT_KEY = 'ojs_device_fingerprint';

/**
 * E2E-only lever (same mechanism as FEATURE_FLAG_OVERRIDES: an ungated storage hook the
 * Cypress suite sets via `win.localStorage`). When present, web generation reports
 * "no identity resolvable" — the exact state JSAD-6922 is about.
 *
 * This hook exists because the state cannot be produced from outside: every Thumbmark
 * entropy component runs in its own try/catch and a failed component is simply EXCLUDED
 * from the hash, so neutering canvas/hardwareConcurrency still yields a real thumbmark
 * from the surviving components (proven live 2026-08-13 — the old spec recipe that broke
 * entropy sources never actually broke generation). Setting this key in prod only makes
 * YOUR device unidentifiable, which now fails closed (that is the JSAD-6922 fix), so it
 * grants nothing.
 */
const E2E_FINGERPRINT_UNAVAILABLE_KEY = 'E2E_DEVICE_FINGERPRINT_UNAVAILABLE';

/**
 * Thrown by {@link requireDeviceFingerprint} when no device identity can be resolved.
 *
 * Callers that bind trust to the fingerprint (MFA send/verify, login, signup) must surface
 * this as a real failure rather than substituting a blank — see the doc on
 * `requireDeviceFingerprint` for why.
 */
export class DeviceFingerprintError extends Error {
    constructor() {
        super('Unable to identify this device.');
        this.name = 'DeviceFingerprintError';
    }
}

const generateDeviceFingerprint = async (): Promise<string | undefined> => {
    // Checked before the cache so the override wins even against a previously stored value.
    if (isWeb && (await storage.getItemAsync(E2E_FINGERPRINT_UNAVAILABLE_KEY))) {
        return undefined;
    }
    const generatedFingerprint = await storage.getItemAsync(DEVICE_FINGERPRINT_KEY);
    if (generatedFingerprint) {
        return generatedFingerprint;
    }
    if (isWeb) {
        const { Thumbmark } = await import('@thumbmarkjs/thumbmarkjs');
        const thumbmark = new Thumbmark({ logging: false });
        const result = await thumbmark.get();
        const fingerprint = result?.thumbmark;
        if (!fingerprint) {
            console.warn('Failed to generate device fingerprint on web');
            Sentry.captureMessage('Failed to generate device fingerprint on web', {
                level: 'warning'
            });
            return;
        }
        await storage.setItemAsync(DEVICE_FINGERPRINT_KEY, fingerprint);
        return fingerprint;
    }
    const { getUniqueId } = await import('react-native-device-info');
    const fingerprint = await getUniqueId();
    if (!fingerprint) {
        console.warn('Failed to generate device fingerprint on native');
        Sentry.captureMessage('Failed to generate device fingerprint on native', {
            level: 'warning'
        });
        return;
    }
    await storage.setItemAsync(DEVICE_FINGERPRINT_KEY, fingerprint);
    return fingerprint;
};

/**
 * In-flight generation, shared by concurrent callers.
 *
 * Generation is read-storage → generate → write-storage with no lock, so two callers that
 * both miss the cache both generate. On web Thumbmark derives from browser entropy and can
 * return *different* values across those two runs, so last-write-wins leaves the stored
 * fingerprint different from the one an in-flight request already sent to the backend —
 * trust gets bound to a value the next request no longer presents (JSAD-6922). `useInit`
 * fires this on app start while auth/checkout can fire it concurrently, so the race is
 * reachable on a cold start.
 *
 * Sharing the promise makes every concurrent caller observe one generation. Cleared on
 * settle so a failure doesn't poison later attempts.
 */
let inflightFingerprint: Promise<string | undefined> | undefined;

export const getDeviceFingerprint = async (): Promise<string | undefined> => {
    if (!inflightFingerprint) {
        inflightFingerprint = generateDeviceFingerprint().finally(() => {
            inflightFingerprint = undefined;
        });
    }
    return inflightFingerprint;
};

/**
 * Device fingerprint for identity-binding calls — MFA send/verify, login, signup.
 *
 * Throws {@link DeviceFingerprintError} instead of returning a blank. Callers used to write
 * `(await getDeviceFingerprint()) ?? ''`, which converts "I could not identify this device"
 * into "this device's id is the empty string". The backend cannot tell those apart: it
 * validates the OTP and binds device trust to `''`, returns success, and the next request
 * arrives with a real fingerprint that matches nothing — so `wallet-info` 428s forever while
 * the user was told verification succeeded (JSAD-6922).
 *
 * Use this wherever the value is bound to trust. `getDeviceFingerprint()` remains correct
 * where the fingerprint is advisory (analytics, best-effort hints) and absence is tolerable.
 */
export const requireDeviceFingerprint = async (): Promise<string> => {
    const fingerprint = await getDeviceFingerprint();
    if (!fingerprint) {
        throw new DeviceFingerprintError();
    }
    return fingerprint;
};

export const getDeviceType = () => {
    return Platform.OS === 'web' ? 'web' : 'mobile';
};

export const getPlatform = async () => {
    switch (Platform.OS) {
        case 'ios':
            return 'iOS';
        case 'android':
            return 'Android';
        case 'web': {
            const { detect } = await import('detect-browser');
            const browser = detect();
            if (!browser) {
                return 'unknown';
            }
            return browser.name ? browser.name : 'unknown';
        }
        default:
            return 'Unknown';
    }
};
